/**
 * @file AskIntroPill.tsx
 * @brief Home's "Ask / Your meetings" quick action on the web and desktop app:
 *        arrives across the whole row with its label, then closes to the
 *        68px icon circle. The phone's intro, ported.
 *
 * @details
 * DES 04-10-2026: "desktop per home per ask wala quick action mobile jaisa
 * animate kiyoon nahin hota". Mirrors FRONTEND
 * screens/Home/components/AskIntroPill.tsx:
 * - Starts a third of the quick-action row wide (the phone uses the whole
 *   row; on a desktop window that read as a glitch, DES 04-10-2026), "Ask"
 *   and "Your meetings" showing, pushing the pills beside it along.
 * - 700ms after Home's data has loaded (`ready`) its real width eases down to
 *   the circle and the neighbours slide back in. At most 5s if the data
 *   never answers. The words fade first, so a
 *   letter never sits clipped on the circle.
 * - Plays on every visit: Home unmounts when you leave it on the web, so each
 *   mount is a visit (the phone needed a focus token for this).
 * - Reduce Motion: no intro, the circle from the start.
 *
 * Geometry is the phone's: 68 tall, logo 40 at 14 in (so the closed circle
 * centres it; DES 09-10-2026, was icon 36 at 16), soft red fill (12%, 22% dark). An ease-out curve instead of the
 * phone's spring: an overshoot would dip the circle below 68px.
 *
 * @author DES
 * @date 04-10-2026
 */

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { AskSpark } from "@/components/icons/AskSpark";
import { cn } from "@/shared/theme/cn";

const CIRCLE = 68;
const START_DELAY_MS = 700;
const CLOSE_MS = 650;
/** A rail that never answers (offline, a slow API) must not leave Ask wide. */
const MAX_WAIT_MS = 5000;
/**
 * Open at least this long after Home appears (DES 04-10-2026: "poora open
 * ho, load honay ka baad collapse karay"): with cached data `ready` is true
 * at once and the pill closed before anyone saw it open.
 */
const MIN_OPEN_MS = 1600;

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

/** Never narrower than this open, so "Your meetings" is never clipped. */
const MIN_OPEN = 180;

/**
 * The open width: a third of the row (DES 04-10-2026: the whole row "glitch
 * lagta hai" on a wide desktop window), but at least MIN_OPEN.
 */
function openWidth(row: HTMLElement): number {
  const cs = getComputedStyle(row);
  const inner = row.clientWidth - parseFloat(cs.paddingLeft || "0") - parseFloat(cs.paddingRight || "0");
  return Math.max(CIRCLE, Math.min(inner, Math.max(MIN_OPEN, Math.round(inner / 3))));
}

export function AskIntroPill({ ready, onClick }: { ready: boolean; onClick: () => void }) {
  const ref = useRef<HTMLButtonElement | null>(null);
  const [reduced] = useState(prefersReducedMotion);
  const [closed, setClosed] = useState(reduced);
  const [wide, setWide] = useState<number | null>(null);

  // Arrival width before the first paint, and kept in step with the window
  // while the pill is still open.
  useLayoutEffect(() => {
    if (reduced) return;
    const row = ref.current?.parentElement;
    if (!row) return;
    setWide(openWidth(row));
    const ro = new ResizeObserver(() => setWide(openWidth(row)));
    ro.observe(row);
    return () => ro.disconnect();
  }, [reduced]);

  const mountedAt = useRef(performance.now());
  useEffect(() => {
    if (closed) return;
    const sinceMount = performance.now() - mountedAt.current;
    const wait = ready
      ? Math.max(START_DELAY_MS, MIN_OPEN_MS - sinceMount)
      : MAX_WAIT_MS - sinceMount;
    const t = window.setTimeout(() => setClosed(true), Math.max(0, wait));
    return () => window.clearTimeout(t);
  }, [ready, closed]);

  const width = closed || wide === null ? CIRCLE : wide;

  return (
    <button
      ref={ref}
      type="button"
      aria-label="Ask AI about your meetings"
      title="Ask AI about your meetings"
      onClick={onClick}
      style={{
        width,
        // Width animates only on the close. The open width can arrive a frame
        // late (the row measures 0 at mount) and must snap, not grow in.
        transition: closed && !reduced
          ? `width ${CLOSE_MS}ms cubic-bezier(0.22, 1, 0.36, 1), background-color 150ms ease`
          : "background-color 150ms ease",
      }}
      className="flex h-[68px] shrink-0 items-center gap-2.5 overflow-hidden rounded-full bg-brand/[0.12] pl-3.5 text-left hover:bg-brand/20 dark:bg-brand/[0.22]"
    >
      {/* The VERBLIZR logo (DES 09-10-2026). It rolls left like a wheel as
          the pill closes onto it: one turn on the same curve and time as the
          width, upright at both ends. Reduce Motion: closed from the start,
          no transition, so it never turns. */}
      <AskSpark
        className="h-10 w-10 shrink-0 text-brand"
        style={{
          transform: closed ? "rotate(-360deg)" : "rotate(0deg)",
          transition: closed && !reduced
            ? `transform ${CLOSE_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`
            : undefined,
        }}
      />
      <span
        aria-hidden
        className={cn(
          "min-w-0 whitespace-nowrap transition-opacity duration-300 ease-out",
          closed ? "opacity-0" : "opacity-100",
        )}
      >
        <span className="block text-[15px] font-semibold text-app-text">Ask AI</span>
        <span className="mt-px block text-xs font-medium text-app-text-secondary">Your meetings</span>
      </span>
    </button>
  );
}
